import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useIsSmallViewport } from "hooks/useIsSmallViewport";
import { faSave } from "@fortawesome/free-solid-svg-icons";
import {
  ItemMenu,
  ItemMenuData,
  MultiSelectAction,
} from "components/UI/ItemMenu/ItemMenu";
import { SortByPicker } from "components/UI/SortByPicker";
import { toGPXTrack, useExportGPX } from "hooks/useExportGPX";
import { useGenericModals } from "hooks/useGenericModals";
import { useUserTracks } from "stores/useUserTracks";
import { useTranslation } from "react-i18next";
import { updateNonSaveableOptions } from "stores/optionsStore/actions";
import { useOptions } from "stores/optionsStore/OptionsContext";
import { useRouting } from "stores/routingStore/RoutingContext";
import { formatDistanceLabel } from "utils/formatters/formatDistance";
import { filterDataByString } from "utils/helpers/filterData";
import { getSortingOptions, sortItems } from "utils/sorting/itemSorting";
import { useGenericDispatch } from "stores/genericStore/GenericContext";
import { rewriteChartTooltipData } from "stores/genericStore/actions";
import TracksWaypointsMenuPlaceholder from "./TracksWaypointsMenuPlaceholder";
import { Text } from "@mantine/core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { rewriteTrackmeta } from "stores/routingStore/actions";

interface ExtendedItemMenuData extends ItemMenuData {
  length: number;
}

const SORT_BYS = ["name", "createdDate", "length"] as const;

export const TracksMenu = () => {
  const {
    tracks,
    hiddenTrackIds,
    selectedTrack,
    selectTrack,
    toggleTrackVisibility,
    isFetching,
    deleteTrack,
    leaveOpenTrack,
  } = useUserTracks();
  const [filterKeyword, setFilterKeyword] = useState("");
  const { t } = useTranslation();
  const {
    dispatch,
    state: { isLeftMenuOpen, units, sortTracks },
    updateSaveable,
  } = useOptions();
  const { dispatch: dispatchRouting } = useRouting();
  const { openConfirmTrackDeleteModal } = useGenericModals();
  const selectedTrackRef = useRef(selectedTrack);
  const tracksRef = useRef(tracks);
  useEffect(() => {
    selectedTrackRef.current = selectedTrack;
  }, [selectedTrack]);
  useEffect(() => {
    tracksRef.current = tracks;
  }, [tracks]);
  const isSmallViewport = useIsSmallViewport();
  const genericDispatch = useGenericDispatch();

  const openOrCloseTrack = useCallback(
    async (id: number) => {
      if (id !== selectedTrackRef.current?.id) {
        if (isLeftMenuOpen && isSmallViewport) {
          selectTrack(null);
          genericDispatch(rewriteChartTooltipData(null));
          dispatchRouting(
            rewriteTrackmeta({
              totalDistance: undefined,
              trackPointsSpeed: undefined,
              trackPointsTime: undefined,
              totalTime: undefined,
            })
          );

          // We need to await here for a bit to prevent race donwstream
          await new Promise((resolve) => {
            setTimeout(() => resolve(undefined), 10);
          });

          dispatch(updateNonSaveableOptions({ isLeftMenuOpen: false }));
        }
        selectTrack(id);
      } else {
        selectTrack(null);
      }
    },
    [
      dispatch,
      dispatchRouting,
      genericDispatch,
      isLeftMenuOpen,
      isSmallViewport,
      selectTrack,
    ]
  );

  // Both close the open own track, which may have a change that isn't saved yet
  const handleMenuItemSelect = useCallback(
    (id: number) => leaveOpenTrack(() => void openOrCloseTrack(id)),
    [leaveOpenTrack, openOrCloseTrack]
  );
  // Switching the multiselect closes the own track open in the editor, whose item would stay highlighted.
  // A new track being drawn isn't in the list, so it stays (emptying the editor would lose it)
  const handleMultiSelectModeToggle = useCallback(() => {
    if (selectedTrackRef.current) leaveOpenTrack(() => selectTrack(null));
  }, [leaveOpenTrack, selectTrack]);

  const handleMenuItemRightButtonClick = useCallback(
    (id: number) => {
      toggleTrackVisibility(id);
    },
    [toggleTrackVisibility]
  );

  const deleteTracks: MultiSelectAction = useCallback(
    (ids, callback) => {
      openConfirmTrackDeleteModal({
        count: ids.length,
        onConfirm: () => {
          void deleteTrack(ids).then(callback);
        },
      });
    },
    [deleteTrack, openConfirmTrackDeleteModal]
  );

  const exportGPX = useExportGPX();
  const exportToGPX: MultiSelectAction = useCallback(
    (ids, done) => {
      const selectedTracks = tracksRef.current?.filter(({ id }) =>
        ids.includes(id)
      );
      if (!selectedTracks) return;
      const exported = exportGPX({
        tracks: selectedTracks.map((track) =>
          toGPXTrack(track, {
            trackPoints: track.trackPoints,
            trackPointsEle: track.trackPointsEle,
          })
        ),
      });
      if (exported) done();
    },
    [exportGPX]
  );

  const data = tracks?.map((track) => {
    const formattedDistance = track.meta?.length
      ? formatDistanceLabel(track.meta.length * 1000, units, 1, t)
      : undefined;

    const itemData: ExtendedItemMenuData = {
      ...track,
      bottomInfo: formattedDistance,
      length: track.meta?.length || 0,
      color: track.color,
    };
    return itemData;
  });

  const filteredData = filterDataByString(data, "name", filterKeyword);

  const sortedData = sortItems(
    filteredData,
    sortTracks.type,
    sortTracks.direction === "asc"
  );

  const SortComponent = useMemo(
    () => (
      <SortByPicker
        data={getSortingOptions(t, SORT_BYS)}
        value={sortTracks.type}
        sortDirection={sortTracks.direction}
        onItemChange={(value, direction) => {
          updateSaveable({ sortTracks: { type: value, direction } });
        }}
      />
    ),
    [sortTracks.direction, sortTracks.type, t, updateSaveable]
  );

  return useMemo(
    () => (
      <ItemMenu
        data={sortedData}
        onDeleteClick={deleteTracks}
        onExportToGPXclick={exportToGPX}
        onMultiSelectModeOn={handleMultiSelectModeToggle}
        onFilterInput={setFilterKeyword}
        selectedItemId={selectedTrack?.id}
        hiddenItemIds={hiddenTrackIds}
        onItemClick={(id) => void handleMenuItemSelect(id)}
        onItemRightButtonClick={handleMenuItemRightButtonClick}
        loading={isFetching}
        placeholder={
          <TracksWaypointsMenuPlaceholder
            emptyText={
              <Text
                p="md"
                c="dimmed"
                size="sm"
                style={{ textAlign: "center", userSelect: "none" }}
              >
                {t("tracks_menu.help_text_01")}
                {t("tracks_menu.help_text_02")}
                {t("tracks_menu.help_text_03a")}{" "}
                <FontAwesomeIcon icon={faSave} color="#7950f2" />{" "}
                {t("tracks_menu.help_text_03b")}
                {t("tracks_menu.help_text_04", {
                  saveText: t("generic.save_button_label"),
                })}
              </Text>
            }
          />
        }
        topSubmenuComponent={SortComponent}
      />
    ),
    [
      hiddenTrackIds,
      SortComponent,
      deleteTracks,
      exportToGPX,
      handleMenuItemRightButtonClick,
      handleMenuItemSelect,
      handleMultiSelectModeToggle,
      isFetching,
      selectedTrack?.id,
      sortedData,
      t,
    ]
  );
};
